import React, { Component } from 'react';
import { isEmpty } from 'lodash';
import fileDownload from 'react-file-download';
import { Loader2 } from 'lucide-react';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
} from '@cargomatic/ui';
import { uploadBulkConsigneeCSVFile } from 'utils/UserWebAPIUtils';
import { useTranslation, withTranslation } from 'react-i18next';
import { FileUploader } from 'components/v2/FileUploader';
import { CSV_FILE_TYPES } from 'config/accepted-file-types';
import CargomaticInput from '../../v2/input';
import UserActionCreators from '../../../actions/UserActionCreators';

const consigneeTemplate = 'company_name,first_name,last_name,email,phone\n,,,,';

const getEmptyState = () => {
  return {
    creatingConsignees: false,
    consignee: {
      company_name: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      phone: '',
    },
  };
};

const OrDivider = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-muted-foreground">
      <Separator className="flex-1" />
      <span>{t('OR')}</span>
      <Separator className="flex-1" />
    </div>
  );
};

class RegisterConsignee extends Component {
  constructor(props) {
    super(props);
    this.state = getEmptyState();
  }

  handleChange = (field) => (value) => {
    this.setState((prevState) => {
      return {
        consignee: {
          ...prevState.consignee,
          [field]: value,
        },
      };
    });
  };

  handleRegister = (e) => {
    e.preventDefault();
    const { consignee } = this.state;
    UserActionCreators.addConsignee({
      ...consignee,
    });
    this.setState(getEmptyState());
  };

  handleFileUpload = (files = []) => {
    if (!files || !files.length) return;
    this.setState({ creatingConsignees: true }, async () => {
      try {
        await uploadBulkConsigneeCSVFile(files[0]);
      } catch (e) {
        console.error('Error', e);
      } finally {
        this.setState({ creatingConsignees: false });
      }
    });
  };

  handleDownloadTemplate = () => {
    fileDownload(consigneeTemplate, 'consignee-template.csv');
  };

  render() {
    const { consignee, creatingConsignees } = this.state;
    const { t } = this.props;
    const disabled =
      isEmpty(consignee.company_name) ||
      isEmpty(consignee.email) ||
      isEmpty(consignee.first_name) ||
      isEmpty(consignee.last_name) ||
      isEmpty(consignee.password) ||
      isEmpty(consignee.phone);

    return (
      <Card>
        <CardHeader>
          <CardTitle>Add Consignee</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Bulk Upload via CSV</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={this.handleDownloadTemplate}
              >
                {t('DOWNLOAD_TEMPLATE')}
              </Button>
            </div>
            <FileUploader
              fileTypes={CSV_FILE_TYPES}
              fileTypesLabel={t('CSV')}
              onChange={this.handleFileUpload}
              multiple={false}
            />
            {creatingConsignees && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t('PROCESSING_CONSIGNEES')}</span>
              </div>
            )}
          </div>
          <OrDivider />
          <form onSubmit={this.handleRegister}>
            <h4 className="mb-4 text-sm font-medium">
              {t('CREATE_CONSIGNEE')}
            </h4>
            <div className="grid grid-cols-4 gap-6 max-w-3xl">
              <div className="col-span-4 sm:col-span-2">
                <CargomaticInput
                  className="mb-4"
                  type="text"
                  value={consignee.first_name}
                  label={t('FIRST_NAME')}
                  placeholder={t('FIRST_NAME')}
                  onChange={this.handleChange('first_name')}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <CargomaticInput
                  className="mb-4"
                  type="text"
                  value={consignee.last_name}
                  label={t('LAST_NAME')}
                  placeholder={t('LAST_NAME')}
                  onChange={this.handleChange('last_name')}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <CargomaticInput
                  className="mb-4"
                  type="text"
                  value={consignee.company_name}
                  label={t('COMPANY_NAME')}
                  placeholder={t('COMPANY_NAME')}
                  onChange={this.handleChange('company_name')}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <CargomaticInput
                  className="mb-4"
                  type="phone"
                  value={consignee.phone}
                  label={t('PHONE')}
                  placeholder={t('PHONE')}
                  onChange={this.handleChange('phone')}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <CargomaticInput
                  className="mb-4"
                  type="email"
                  value={consignee.email}
                  label={t('EMAIL')}
                  placeholder={t('EMAIL')}
                  onChange={this.handleChange('email')}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <CargomaticInput
                  className="mb-4"
                  type="password"
                  placeholder={t('PASSWORD')}
                  value={consignee.password}
                  label={t('PASSWORD')}
                  onChange={this.handleChange('password')}
                />
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <Button disabled={disabled} type="submit">
                {t('REGISTER_CONSIGNEE')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }
}

export default withTranslation()(RegisterConsignee);
